import { supabase } from '@/lib/supabase';
import { ORDER_STATUSES_WITH_PENDING_STOCK } from '@/lib/orderStatus';

// Helper seguro para detectar producción en Vite/Browser
const isProduction = () => {
  try {
    // Vite expone import.meta.env.PROD
    // Fallback a process.env solo si existe
    return (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.PROD) ||
           (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production');
  } catch (_) {
    return false;
  }
};
import { sanitizeProductData, sanitizeInventoryData } from '@/lib/utils';

// Cache para almacenar datos en memoria
let inventoryCache = null;
let productsCache = null;
let lastFetchTime = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

// Función para limpiar el caché de productos
export const clearProductsCache = () => {
  productsCache = null;
};

export const fetchInventoryOptimized = async (forceRefresh = false) => {
  const now = Date.now();
  
  // Usar caché si está disponible y no ha expirado
  if (!forceRefresh && inventoryCache && (now - lastFetchTime) < CACHE_DURATION) {
    return inventoryCache;
  }

  try {
    // Paginación para evitar límite de 1000 filas de PostgREST
    const PAGE_SIZE = 1000;
    let offset = 0;
    let allRows = [];
    /* eslint-disable no-constant-condition */
    while (true) {
      const { data: page, error } = await supabase
        .from('inventory')
        .select(`
          id,
          product_id,
          size,
          color,
          quantity,
          pending_orders,
          created_at,
          updated_at
        `)
        .order('product_id', { ascending: true })
        .range(offset, offset + PAGE_SIZE - 1);
      if (error) throw error;
      allRows = allRows.concat(page || []);
      if (!page || page.length < PAGE_SIZE) break;
      offset += PAGE_SIZE;
    }

    // Sanitizar datos antes de cachear
    const sanitizedData = sanitizeInventoryData(allRows || []);
    inventoryCache = sanitizedData;
    lastFetchTime = now;
    try {
      const debugId = (typeof localStorage !== 'undefined' && localStorage.getItem('inventoryDebugId')) || 'a5006d2b-8a31-444b-8c13-99fa2798ce08';
      const hasDebug = sanitizedData.some(r => String(r.product_id) === String(debugId));
      const sample = sanitizedData.slice(0, 3);
      if (!isProduction()) {
        // eslint-disable-next-line no-console
        console.log('🔎 INV-DEBUG fetchInventoryOptimized', { count: sanitizedData.length, hasDebug, debugId, sample, pages: Math.ceil((allRows.length || 0) / PAGE_SIZE) });
      }
    } catch (_) {}
    return inventoryCache;
  } catch (error) {
    console.error('Error fetching inventory:', error);
    throw error;
  }
};

export const fetchProductsOptimized = async (forceRefresh = false) => {
  if (!forceRefresh && productsCache) {
    return productsCache;
  }

  try {
    // Consulta optimizada incluyendo imágenes
    const { data, error } = await supabase
      .from('products')
      .select(`
        id,
        code,
        description,
        price,
        temporada,
        sizes,
        colors,
        images,
        main_image_index,
        active,
        created_at,
        updated_at
      `)
      .eq('active', true)
      .order('code', { ascending: true });

    if (error) throw error;

    // Sanitizar datos antes de cachear
    const sanitizedData = (data || []).map(product => sanitizeProductData(product));
    productsCache = sanitizedData;
    
    if (!isProduction()) {
      console.log('📊 Diagnóstico de productos:');
      console.log(`- Total de productos activos: ${sanitizedData.length}`);
    }
    
    return productsCache;
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

// Función separada para obtener imágenes de productos cuando sea necesario
export const fetchProductImages = async (productIds) => {
  if (!productIds || productIds.length === 0) return {};
  
  try {
    const { data, error } = await supabase
      .from('products')
      .select('id, images, main_image_index')
      .in('id', productIds);

    if (error) throw error;

    const imagesMap = {};
    data?.forEach(product => {
      try {
        const sanitizedProduct = sanitizeProductData(product);
        imagesMap[product.id] = {
          images: sanitizedProduct.images || [],
          main_image_index: sanitizedProduct.main_image_index || 0
        };
      } catch (productError) {
        console.warn('Error sanitizing product images for product', product.id, productError);
        imagesMap[product.id] = {
          images: [],
          main_image_index: 0
        };
      }
    });

    return imagesMap;
  } catch (error) {
    console.error('Error fetching product images:', error);
    return {};
  }
};

export const fetchPendingOrdersOptimized = async () => {
  try {
    // Tomar exclusivamente líneas con pendiente > 0 (paginado)
    const PAGE_SIZE = 1000;
    let offset = 0;
    let allItems = [];
    /* eslint-disable no-constant-condition */
    while (true) {
      const { data: page, error } = await supabase
        .from('order_items')
        .select('id, order_id, product_id, size, color, pending_quantity, orders!inner(status)')
        .gt('pending_quantity', 0)
        .in('orders.status', ORDER_STATUSES_WITH_PENDING_STOCK)
        .range(offset, offset + PAGE_SIZE - 1);
      if (error) throw error;
      allItems = allItems.concat(page || []);
      if (!page || page.length < PAGE_SIZE) break;
      offset += PAGE_SIZE;
    }

    // Agrupar por order_id para mantener el formato esperado aguas abajo
    const byOrder = new Map();
    for (const it of (allItems || [])) {
      const list = byOrder.get(it.order_id) || [];
      list.push({
        id: it.id,
        product_id: it.product_id,
        size: it.size,
        color: it.color,
        pending_quantity: Number(it.pending_quantity) || 0
      });
      byOrder.set(it.order_id, list);
    }

    const normalized = Array.from(byOrder.entries()).map(([orderId, lines]) => ({ id: orderId, items: lines }));

    if (!isProduction()) {
      console.log('📊 Diagnóstico de líneas con pendiente (>0)');
      console.log(`- Líneas con pendiente > 0 (total): ${allItems.length}`);
      console.log(`- Pedidos con líneas pendientes: ${normalized.length}`);
    }
    
    return normalized;
  } catch (error) {
    console.error('Error fetching pending orders:', error);
    throw error;
  }
};

// Función optimizada para recálculo por lotes
export const recalculateInventoryOptimized = async (progressCallback = null) => {
  try {
    // Paso 1: Obtener pedidos pendientes ANTES de eliminar el inventario
    if (progressCallback) progressCallback('Obteniendo pedidos pendientes...', 5);
    
    const pendingOrders = await fetchPendingOrdersOptimized();
    const pendingUpdates = new Map();

    // Calcular pedidos pendientes por producto/talla/color
    if (!isProduction()) console.log('🔍 Procesando pedidos pendientes...');
    let totalPendingQuantity = 0;
    
    for (const order of pendingOrders) {
      for (const item of order.items || []) {
        const qty = Number(item.pending_quantity) || 0;
        if (qty > 0) {
          const key = `${item.product_id}::${item.size}::${item.color}`;
          const current = pendingUpdates.get(key) || 0;
          const newTotal = current + qty;
          pendingUpdates.set(key, newTotal);
          totalPendingQuantity += qty;
        }
      }
    }
    
    if (!isProduction()) {
      console.log(`📦 Total de combinaciones producto/talla/color con pedidos: ${pendingUpdates.size}`);
      console.log(`📦 Total de unidades pendientes: ${totalPendingQuantity}`);
    }

    // Paso 2: (Mantener) No borrar inventario aquí; se hará dentro de RPC replace_inventory

    // Paso 3: Obtener todos los documentos de entrada y salida
    if (progressCallback) progressCallback('Obteniendo documentos...', 20);
    
    const [entradasResult, salidasResult] = await Promise.all([
      supabase
        .from('documents')
        .select(`
          id,
          type,
          date,
          items:document_items(
            id,
            product_id,
            size,
            color,
            quantity
          )
        `)
        .eq('type', 'entrada')
        .eq('status', 'completed')
        .order('date', { ascending: true }),
      
      supabase
        .from('documents')
        .select(`
          id,
          type,
          date,
          items:document_items(
            id,
            product_id,
            size,
            color,
            quantity
          )
        `)
        .eq('type', 'salida')
        .eq('status', 'completed')
        .order('date', { ascending: true })
    ]);

    if (entradasResult.error) throw entradasResult.error;
    if (salidasResult.error) throw salidasResult.error;

    // Paso 4: Obtener todos los productos activos para asegurar cobertura completa
    if (progressCallback) progressCallback('Obteniendo productos activos...', 35);
    
    const { data: productsData, error: productsError } = await supabase
      .from('products')
      .select('id, sizes, colors')
      .eq('active', true);
    
    if (productsError) throw productsError;
    
    // Crear mapa de inventario inicial con todos los productos activos
    const inventoryMap = new Map();
    
    // Inicializar inventario con todos los productos activos (stock 0)
    for (const product of productsData || []) {
      const sizes = product.sizes || [];
      const colors = product.colors || [];
      
      for (const size of sizes) {
        for (const color of colors) {
          const key = `${product.id}::${size}::${color}`;
          inventoryMap.set(key, {
            product_id: product.id,
            size: size,
            color: color,
            quantity: 0,
            pending_orders: 0
          });
        }
      }
    }
    
    if (!isProduction()) {
      console.log(`📦 Productos activos inicializados: ${productsData?.length || 0}`);
      console.log(`📦 Combinaciones producto/talla/color inicializadas: ${inventoryMap.size}`);
    }

    // Paso 5: Procesar documentos de entrada
    if (progressCallback) progressCallback('Procesando documentos de entrada...', 40);
    
    // Procesar entradas
    for (const entrada of entradasResult.data || []) {
      for (const item of entrada.items || []) {
        const key = `${item.product_id}::${item.size}::${item.color}`;
        const current = inventoryMap.get(key) || { quantity: 0, pending_orders: 0 };
        const qty = Number(item.quantity) || 0;
        inventoryMap.set(key, {
          ...current,
          product_id: item.product_id,
          size: item.size,
          color: item.color,
          quantity: (Number(current.quantity) || 0) + qty
        });
      }
    }

    // Paso 6: Procesar documentos de salida
    if (progressCallback) progressCallback('Procesando documentos de salida...', 60);
    
    // Procesar salidas
    for (const salida of salidasResult.data || []) {
      for (const item of salida.items || []) {
        const key = `${item.product_id}::${item.size}::${item.color}`;
        const current = inventoryMap.get(key) || { quantity: 0, pending_orders: 0 };
        const qty = Number(item.quantity) || 0;
        inventoryMap.set(key, {
          ...current,
          product_id: item.product_id,
          size: item.size,
          color: item.color,
          quantity: (Number(current.quantity) || 0) - qty
        });
      }
    }

    // Paso 7: Aplicar pedidos pendientes al mapa ANTES de insertar (fuente: líneas de order_items)
    if (progressCallback) progressCallback('Aplicando pedidos pendientes...', 75);
    for (const [key, pendingQuantity] of pendingUpdates) {
      const current = inventoryMap.get(key);
      if (current) {
        current.pending_orders = pendingQuantity;
        inventoryMap.set(key, current);
      } else {
        // Si no existe la combinación, crearla con stock 0 para que el inventario refleje el pendiente
        const [productId, size, color] = key.split('::');
        inventoryMap.set(key, {
          product_id: productId, // mantener UUID como texto
          size,
          color,
          quantity: 0,
          pending_orders: Number(pendingQuantity) || 0
        });
      }
    }

    // Paso 8: Insertar inventario utilizando RPC transaccional (fallback a insert si no existe)
    if (progressCallback) progressCallback('Insertando inventario...', 90);

    const inventoryData = Array.from(inventoryMap.values()).map(row => ({
      ...row,
      quantity: Number(row.quantity) || 0,
      pending_orders: Number(row.pending_orders) || 0
    }));

    if (!isProduction()) {
      console.log('📊 Diagnóstico de inventario:');
      console.log(`- Total de combinaciones producto/talla/color en inventario: ${inventoryData.length}`);
      console.log(`- Total de productos únicos: ${new Set(inventoryData.map(item => item.product_id)).size}`);
    }

    if (inventoryData.length > 0) {
      // Intentar RPC primero
      const { data: rpcData, error: rpcError } = await supabase.rpc('replace_inventory', {
        inventory_data: inventoryData
      });
      if (rpcError) {
        if (!isProduction()) console.warn('RPC replace_inventory no disponible, usando insert directo', rpcError);
        // Fallback: limpiar e insertar en una transacción manual
        const { error: deleteBeforeInsert } = await supabase
          .from('inventory')
          .delete()
          .not('product_id', 'is', null);
        if (deleteBeforeInsert) throw deleteBeforeInsert;
        const { error: insertError } = await supabase
          .from('inventory')
          .insert(inventoryData);
        if (insertError) throw insertError;
      }
    }

    // Limpiar caché
    inventoryCache = null;
    productsCache = null;

    if (progressCallback) progressCallback('¡Recálculo completado!', 100);
    
    return { success: true, processedItems: inventoryData.length };
  } catch (error) {
    console.error('Error in optimized inventory recalculation:', error);
    throw error;
  }
};

// Función para obtener estadísticas rápidas del inventario
export const getInventoryStats = async () => {
  try {
    const { data, error } = await supabase
      .from('inventory')
      .select(`
        quantity,
        pending_orders,
        product:products(price)
      `);

    if (error) throw error;

    const stats = {
      totalStock: 0,
      totalPending: 0,
      totalValue: 0,
      itemsWithStock: 0,
      itemsWithPending: 0
    };

    data?.forEach(item => {
      const quantity = parseInt(item.quantity) || 0;
      const pending = parseInt(item.pending_orders) || 0;
      const price = item.product?.price || 0;

      stats.totalStock += quantity;
      stats.totalPending += pending;
      stats.totalValue += quantity * price;
      
      if (quantity > 0) stats.itemsWithStock++;
      if (pending > 0) stats.itemsWithPending++;
    });

    return stats;
  } catch (error) {
    console.error('Error getting inventory stats:', error);
    throw error;
  }
};

// Función para limpiar caché
export const clearInventoryCache = () => {
  inventoryCache = null;
  productsCache = null;
  lastFetchTime = 0;
};

// Función para actualizar pending_orders en inventario de manera atómica
export const updateInventoryPendingOrders = async (updates, operation = 'add') => {
  try {
    // Intentar usar la función RPC primero
    const { data, error } = await supabase.rpc('update_inventory_pending_orders', {
      updates_data: updates,
      operation_type: operation
    });

    if (error) {
      // Si hay error, usar el método fallback
      console.log('RPC function not available, using fallback method...');
      return await updateInventoryPendingOrdersFallback(updates, operation);
    }

    // Limpiar caché después de la actualización
    clearInventoryCache();
    
    return data;
  } catch (error) {
    // Si hay cualquier error, usar el método fallback
    console.log('Error with RPC, using fallback method...');
    return await updateInventoryPendingOrdersFallback(updates, operation);
  }
};

// Método fallback para actualizar pending_orders
const INVENTORY_FALLBACK_CHUNK_SIZE = 50;
const buildInventoryKey = (productId, size, color) => `${String(productId)}::${String(size)}::${String(color)}`;
const encodeFilterValue = (value) => encodeURIComponent(String(value ?? '').trim());

const chunkArray = (array, size) => {
  if (!Array.isArray(array) || array.length === 0) return [];
  if (size <= 0) return [array];
  const chunks = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
};

const updateInventoryPendingOrdersFallback = async (updates, operation) => {
  try {
    console.log(`🔄 Using fallback method for ${operation} operation with ${updates.length} items`);

    if (!updates || updates.length === 0) {
      return { success: true, updated: 0 };
    }

    const aggregated = new Map();

    for (const item of updates) {
      const { product_id, size, color } = item || {};
      const delta = Number(item?.pending_orders) || 0;

      if (!product_id || !size || !color || delta === 0) {
        continue;
      }

      const key = buildInventoryKey(product_id, size, color);

      if (operation === 'replace') {
        aggregated.set(key, { product_id, size, color, pending_orders: delta });
        continue;
      }

      const current = aggregated.get(key);
      const accumulated = (current?.pending_orders || 0) + delta;
      aggregated.set(key, { product_id, size, color, pending_orders: accumulated });
    }

    if (aggregated.size === 0) {
      return { success: true, updated: 0 };
    }

    const aggregatedEntries = Array.from(aggregated.values());
    const chunks = chunkArray(aggregatedEntries, INVENTORY_FALLBACK_CHUNK_SIZE);

    for (const chunk of chunks) {
      if (!chunk || chunk.length === 0) continue;

      const orFilter = chunk
        .map(({ product_id, size, color }) => `and(product_id.eq.${encodeFilterValue(product_id)},size.eq.${encodeFilterValue(size)},color.eq.${encodeFilterValue(color)})`)
        .join(',');

      let existingRows = [];

      if (orFilter.length > 0) {
        const { data: currentData, error: selectError } = await supabase
          .from('inventory')
          .select('product_id, size, color, pending_orders, quantity')
          .or(orFilter);

        if (selectError) {
          console.error('❌ Error fetching inventory in fallback:', selectError);
          throw selectError;
        }

        existingRows = currentData || [];
      }

      const existingMap = new Map(
        existingRows.map(row => [
          buildInventoryKey(row.product_id, row.size, row.color),
          {
            pending_orders: Number(row.pending_orders) || 0,
            quantity: Number(row.quantity) || 0
          }
        ])
      );

      const rowsToUpsert = [];

      for (const entry of chunk) {
        const key = buildInventoryKey(entry.product_id, entry.size, entry.color);
        const current = existingMap.get(key) || null;
        const currentPending = current?.pending_orders || 0;
        const currentQuantity = current?.quantity || 0;
        const delta = Number(entry.pending_orders) || 0;

        let newPending;

        if (operation === 'add') {
          newPending = currentPending + delta;
        } else if (operation === 'subtract') {
          if (!current) {
            continue;
          }
          newPending = Math.max(0, currentPending - delta);
        } else if (operation === 'replace') {
          newPending = delta;
        } else {
          throw new Error(`Operación no válida: ${operation}`);
        }

        if (newPending === currentPending) {
          continue;
        }

        if (!current && operation !== 'add' && newPending <= 0) {
          continue;
        }

        rowsToUpsert.push({
          product_id: entry.product_id,
          size: entry.size,
          color: entry.color,
          pending_orders: Math.max(0, newPending),
          quantity: currentQuantity
        });
      }

      if (rowsToUpsert.length === 0) {
        continue;
      }

      const { error: upsertError } = await supabase
        .from('inventory')
        .upsert(rowsToUpsert, { onConflict: ['product_id', 'size', 'color'] });

      if (upsertError) {
        console.error('❌ Error updating inventory in fallback:', upsertError);
        throw upsertError;
      }
    }

    clearInventoryCache();
    console.log(`✅ Fallback method completed successfully for ${aggregatedEntries.length} items`);
    return { success: true, updated: aggregatedEntries.length };
  } catch (error) {
    console.error('❌ Error in fallback update method:', error);
    throw error;
  }
};
